// ReportsDashboardPage — the Reports module landing page (`/reports`).
//
// A simple index that surfaces the three module groups (Collections / Skip
// Management / Weighbridge) as cards, each showing how many reports live in
// that group and linking to the first report in it. The group/report data is
// shared with the sidebar via REPORTS_GROUPS so the two never drift.

import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import type { UserSummary } from '../../types/api';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { useAuthStore } from '../../store/authStore';
import { REPORTS_GROUPS } from './ReportsNav';
import styles from './ReportsDashboardPage.module.css';

type Role = UserSummary['role'];

export function ReportsDashboardPage(): JSX.Element {
  const role = useAuthStore((s) => s.user?.role);

  function visible(roles?: Role[]): boolean {
    if (!roles) return true;
    if (!role) return false;
    return roles.includes(role);
  }

  const groups = REPORTS_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((it) => visible(it.roles)),
  }));

  return (
    <div className={styles.page}>
      <PageHeader
        title="Reports"
        subtitle="Operational insights, summaries, and exports across every module"
      />

      <ul className={styles.grid}>
        {groups.map((group) => {
          const count = group.items.length;
          const first = group.items[0];
          const inner = (
            <>
              <span className={styles.icon} aria-hidden>
                <group.icon size={24} />
              </span>
              <span className={styles.text}>
                <span className={styles.title}>{group.label}</span>
                <span className={styles.count}>
                  {count} report{count === 1 ? '' : 's'}
                </span>
              </span>
              <span className={styles.chevron} aria-hidden>
                <ChevronRight size={18} />
              </span>
            </>
          );
          return (
            <li key={group.key}>
              {first ? (
                <Link to={first.to} className={styles.card}>
                  {inner}
                </Link>
              ) : (
                <div className={`${styles.card} ${styles.cardDisabled}`}>{inner}</div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
